import React, { useEffect, useMemo, useState } from "react";
import {
  ExternalLink,
  Globe2,
  Mail,
  Phone,
  Send,
  Share2,
  ShoppingBag,
} from "lucide-react";
import api from "../api/api";
import { useApp } from "../context/AppContext";
import WhatsAppIcon from "../components/WhatsAppIcon";

const CONTACT_CONFIG = {
  phone: {
    title: "Phone",
    Icon: Phone,
    actionText: "Call",
    iconClass: "bg-emerald-50 text-emerald-600",
    buttonClass: "bg-emerald-600 hover:bg-emerald-700",
    href: (value) => `tel:${cleanPhone(value)}`,
  },
  whatsapp: {
    title: "WhatsApp",
    Icon: WhatsAppIcon,
    actionText: "Chat",
    iconClass: "bg-green-50 text-green-600",
    buttonClass: "bg-green-600 hover:bg-green-700",
    href: (value, message) => `https://wa.me/${formatWhatsappNumber(value)}?text=${encodeURIComponent(message)}`,
  },
  email: {
    title: "Mail",
    Icon: Mail,
    actionText: "Mail",
    iconClass: "bg-rose-50 text-rose-600",
    buttonClass: "bg-rose-600 hover:bg-rose-700",
    href: (value) => `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(value.trim())}`,
  },
};

function cleanPhone(value) {
  return String(value || "").replace(/[^\d+]/g, "");
}

function formatWhatsappNumber(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 10 ? `91${digits}` : digits;
}

function getValues(contact) {
  return ["ContactValue1", "ContactValue2", "ContactValue3"]
    .map((key) => contact?.[key])
    .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
    .map((value) => String(value).trim());
}

export default function ContactAnnam() {
  const { user } = useApp();
  const [contacts, setContacts] = useState([]);
  const [shareLinks, setShareLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);
        setMessage("");

        const res = await api.GetContactUs("ADMIN");
        const rows =
          res?.data?.GetContactUsjs?.Table ||
          res?.GetContactUsjs?.Table ||
          [];
        const shareData =
          res?.data?.GetContactUsjs?.Table1?.[0] ||
          res?.GetContactUsjs?.Table1?.[0] ||
          {};

        setContacts(Array.isArray(rows) ? rows : []);
        setShareLinks(shareData || {});
      } catch (err) {
        console.error("Contact fetch failed:", err);
        setMessage("Failed to load contact details. Try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  const contactSections = useMemo(() => {
    return contacts
      .map((contact) => {
        const type = String(contact?.ContactType || "").toLowerCase();
        const config = CONTACT_CONFIG[type];
        const values = getValues(contact);

        if (!config || values.length === 0) return null;
        return { type, ...config, values };
      })
      .filter(Boolean);
  }, [contacts]);

  const appLinks = useMemo(() => {
    return [
      {
        label: "Website",
        value: shareLinks.WebApp,
        text: shareLinks.WebText || "Try Annam Home Foods Site",
        Icon: Globe2,
      },
      {
        label: "Play Store",
        value: shareLinks.MobileApp,
        text: shareLinks.MobileText || "Try Annam Home Foods app",
        Icon: ShoppingBag,
      },
    ].filter((link) => link.value !== null && link.value !== undefined && String(link.value).trim() !== "");
  }, [shareLinks]);

  const shareApp = async (link) => {
    const shareUrl = String(link?.value || "").trim();
    if (!shareUrl) return;
    const shareText = String(link?.text || "").trim();

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Annam Home Foods",
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }

    const whatsappText = [shareText, shareUrl].filter(Boolean).join("\n");
    window.open(`https://wa.me/?text=${encodeURIComponent(whatsappText)}`, "_blank", "noopener,noreferrer");
  };

  const customerName = user?.Vname || user?.name || "Customer";
  const whatsappMessage = `Hi Annam, this is ${customerName}.`;

  return (
    <div className="w-full max-w-md mx-auto overflow-hidden bg-white border border-gray-100 shadow-lg animate-fade-in md:max-w-2xl rounded-2xl">
      <div className="px-5 py-5 bg-white border-b border-gray-100">
        <p className="text-xs font-semibold tracking-wide text-red-500 uppercase">Support</p>
        <h2 className="mt-1 text-2xl font-bold text-gray-900">Contact Annam</h2>
      </div>

      {loading ? (
        <div className="px-5 py-10 text-center text-gray-500">Loading...</div>
      ) : message ? (
        <div className="px-5 py-10 text-center text-red-500">{message}</div>
      ) : contactSections.length === 0 && appLinks.length === 0 ? (
        <div className="px-5 py-10 text-center text-gray-500">No contact details available.</div>
      ) : (
        <div className="p-4 space-y-4 bg-gray-50">
          {contactSections.map((section) => (
            <div key={section.type} className="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                <span className={`flex items-center justify-center w-10 h-10 rounded-xl ${section.iconClass}`}>
                  <section.Icon size={20} strokeWidth={2.2} />
                </span>
                <div>
                  <h3 className="text-base font-bold text-gray-900">{section.title}</h3>
                  <p className="text-xs text-gray-500">
                    {section.values.length} {section.values.length === 1 ? "option" : "options"} available
                  </p>
                </div>
              </div>

              <div className="divide-y divide-gray-100">
                {section.values.map((value, index) => (
                  <a
                    key={`${section.type}-${value}`}
                    href={section.href(value, whatsappMessage)}
                    target={["whatsapp", "email"].includes(section.type) ? "_blank" : undefined}
                    rel={["whatsapp", "email"].includes(section.type) ? "noreferrer" : undefined}
                    className="flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-gray-50"
                  >
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">
                        {section.values.length > 1 ? `${section.title} ${index + 1}` : section.title}
                      </p>
                      <p className="mt-0.5 text-sm font-semibold text-gray-900 break-all">{value}</p>
                    </div>
                    <span className={`flex items-center justify-center flex-none w-10 h-10 text-white transition-colors rounded-full ${section.buttonClass}`}>
                      {section.type === "email" ? <Send size={18} /> : <section.Icon size={18} />}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          ))}

          {appLinks.length > 0 && (
            <div className="overflow-hidden bg-white border border-red-100 shadow-sm rounded-xl">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-red-100 bg-red-50">
                <span className="flex items-center justify-center w-10 h-10 text-red-600 bg-white rounded-xl">
                  <Share2 size={20} strokeWidth={2.2} />
                </span>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Share App With Your Friends</h3>
                  <p className="text-xs text-gray-600">Send Annam app links instantly</p>
                </div>
              </div>

              <div className="divide-y divide-gray-100">
                {appLinks.map((link) => (
                  <button
                    key={link.label}
                    type="button"
                    onClick={() => shareApp(link)}
                    className="flex items-center justify-between w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                  >
                    <span className="flex items-center min-w-0 gap-3">
                      <span className="flex items-center justify-center flex-none text-gray-600 bg-gray-100 rounded-lg w-9 h-9">
                        <link.Icon size={18} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-gray-900">{link.label}</span>
                        <span className="block text-xs text-gray-500 truncate">{link.value}</span>
                      </span>
                    </span>
                    <span className="flex items-center justify-center flex-none text-red-600 rounded-full w-9 h-9 bg-red-50">
                      <ExternalLink size={17} />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
